import { Controller } from "@hotwired/stimulus";

// progress-poll controller
// Attached to the hold overlay after answer submission. Polls a lightweight
// status endpoint every interval to detect if the user has fallen behind the
// survey live pointer and triggers a frame (or page) reload when a change occurs.
export default class extends Controller {
  static values = {
    pollUrl: String,
    currentQuestion: Number,
    // Initial base interval (ms) before backoff. Keep small for fast detection.
    interval: { type: Number, default: 4000 },
    // Maximum interval ceiling after backoff (ms)
    maxInterval: { type: Number, default: 12000 },
    // Backoff multiplier applied after each unchanged tick (capped by maxInterval)
    backoffFactor: { type: Number, default: 1.4 },
    // Jitter percent (0.15 = ±15%) to de-synchronize herd
    jitterRatio: { type: Number, default: 0.15 }
  };

  connect() {
    this.active = true;
    this.start();
  }

  disconnect() {
    this.stop();
  }

  start() {
    if (this.timer) return;
    this.currentInterval = this.intervalValue; // dynamic working interval
    this.scheduleNext();
  }

  stop() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.active = false;
  }

  scheduleNext() {
    if (!this.active) return;
    const jitterPortion = this.currentInterval * this.jitterRatioValue;
    const low = this.currentInterval - jitterPortion;
    const high = this.currentInterval + jitterPortion;
    const nextDelay = Math.max(1000, Math.floor(low + Math.random() * (high - low)));
    this.timer = setTimeout(() => this.tick(), nextDelay);
  }

  tick() {
    if (!this.active) return;
    const url = this.pollUrlValue || '/user_interface/status';
    fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        // CRITICAL: Check active flag again after async fetch completes.
        // If controller was disconnected during fetch (e.g., Turbo Stream updated
        // the hold overlay), we must NOT trigger a reload that would overwrite
        // the correct response. This fixes the race condition where two responses
        // arrive ~3 seconds apart, with the second (poll-triggered) overwriting
        // the first (correct form submission response).
        if (!this.active) return;
        if (!data || typeof data.current_question === 'undefined') return;
        const latest = parseInt(data.current_question, 10);
        if (latest && this.currentQuestionValue && latest !== this.currentQuestionValue) {
          this.stop();
          const userIdMeta = document.querySelector('meta[name="current-user-id"]');
          let frameId = 'respondent-question';
          if (userIdMeta) {
            const uid = userIdMeta.getAttribute('content');
            if (uid) frameId = `user_${uid}_question`;
          }
          const frame = document.getElementById(frameId);
          if (frame && frame.tagName === 'TURBO-FRAME') {
            if (frame.src) {
              frame.src = frame.src.split('?')[0] + '?t=' + Date.now();
            } else {
              window.Turbo && Turbo.visit('/user_interface/new?t=' + Date.now(), { frame: frameId });
            }
          } else {
            window.location.reload();
          }
        } else {
          // No change detected: back off (capped) for subsequent polls
          this.currentInterval = Math.min(
            this.maxIntervalValue,
            Math.floor(this.currentInterval * this.backoffFactorValue)
          );
        }
      })
      .catch(() => {
        // On error, mildly back off to avoid hammering during transient failures
        this.currentInterval = Math.min(this.maxIntervalValue, Math.floor(this.currentInterval * 1.2));
      })
      .finally(() => {
        if (this.active) this.scheduleNext();
      });
  }
}
